/**
 * PremiumSuccessPage — Shown after successful Instamojo payment
 * Extracts payment_id and payment_request_id from URL, fetches activation code
 */
import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL?.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const PremiumSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [orderData, setOrderData] = useState(null);

  const paymentId = searchParams.get('payment_id');
  const paymentStatus = searchParams.get('payment_status');
  const paymentRequestId = searchParams.get('payment_request_id');

  useEffect(() => {
    if (paymentStatus === 'Credit' && paymentRequestId) {
      // Fetch the activation code from our server
      fetch(`${API}/premium/status-by-payment/${paymentRequestId}`)
        .then(r => r.json())
        .then(data => {
          if (data.activation_code) {
            setOrderData(data);
            setStatus('success');
          } else {
            // Code might not be generated yet (webhook delay), show manual instructions
            setStatus('pending');
          }
        })
        .catch(() => setStatus('pending'));
    } else if (paymentStatus === 'Failed') {
      setStatus('failed');
    } else {
      setStatus('pending');
    }
  }, [paymentStatus, paymentRequestId]);

  return (
    <div style={{ minHeight: '80vh', backgroundColor: '#f1f8e9', padding: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ maxWidth: '520px', width: '100%', backgroundColor: 'white', borderRadius: '16px', padding: '32px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', textAlign: 'center' }}>
        
        {status === 'loading' && (
          <div>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <h2 style={{ color: '#1B5E20' }}>Processing your payment...</h2>
            <p style={{ color: '#666' }}>Please wait while we verify your payment.</p>
          </div>
        )}

        {status === 'success' && orderData && (
          <div>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
            <h2 style={{ color: '#1B5E20', marginBottom: '8px' }}>Payment Successful!</h2>
            <p style={{ color: '#666', marginBottom: '24px' }}>Thank you for upgrading to Premium!</p>
            
            <div style={{ background: '#E8F5E9', border: '3px solid #1B5E20', borderRadius: '12px', padding: '20px', margin: '16px 0' }}>
              <p style={{ fontSize: '13px', color: '#666', margin: '0 0 8px' }}>Your Activation Code:</p>
              <h1 style={{ color: '#1B5E20', fontSize: '32px', letterSpacing: '3px', margin: '0', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {orderData.activation_code}
              </h1>
              <button 
                onClick={() => {
                  navigator.clipboard.writeText(orderData.activation_code);
                  alert('Code copied!');
                }}
                style={{ marginTop: '12px', padding: '8px 24px', background: '#1B5E20', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 700 }}
              >
                📋 Copy Code
              </button>
            </div>

            <div style={{ background: '#FFF3E0', borderRadius: '8px', padding: '16px', margin: '16px 0', textAlign: 'left' }}>
              <p style={{ fontWeight: 700, color: '#E65100', margin: '0 0 8px' }}>📱 How to activate:</p>
              <ol style={{ margin: '0', paddingLeft: '20px', color: '#5D4037', fontSize: '14px', lineHeight: '1.8' }}>
                <li>Open <b>Paranthaman</b> app on your phone</li>
                <li>Go to <b>Premium Store</b> page</li>
                <li>Click <b>"🔑 Enter VIP / Patron Code"</b></li>
                <li>Paste the code above and click <b>Apply</b></li>
              </ol>
            </div>

            <p style={{ fontSize: '13px', color: '#888', margin: '16px 0 0' }}>
              📧 This code has also been sent to your email.<br/>
              Valid for 1 year. Maximum 2 devices.
            </p>
          </div>
        )}

        {status === 'pending' && (
          <div>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📧</div>
            <h2 style={{ color: '#FF8F00', marginBottom: '8px' }}>Payment Received!</h2>
            <p style={{ color: '#666', marginBottom: '16px' }}>
              Your activation code will be sent to your email within a few minutes.
            </p>
            <div style={{ background: '#FFF3E0', borderRadius: '8px', padding: '16px', margin: '16px 0' }}>
              <p style={{ fontSize: '14px', color: '#E65100', margin: 0 }}>
                <b>Payment ID:</b> {paymentId || 'Processing...'}<br/>
                If you don't receive the code within 10 minutes, please contact us at <b>svknvs@gmail.com</b> or WhatsApp <b>+91 86107 36302</b>
              </p>
            </div>
          </div>
        )}

        {status === 'failed' && (
          <div>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
            <h2 style={{ color: '#C62828', marginBottom: '8px' }}>Payment Failed</h2>
            <p style={{ color: '#666', marginBottom: '16px' }}>
              Your payment was not completed. No amount has been deducted.
            </p>
            <Link to="/premium-store" style={{ display: 'inline-block', padding: '12px 32px', background: '#1B5E20', color: 'white', borderRadius: '8px', textDecoration: 'none', fontWeight: 700 }}>
              🔄 Try Again
            </Link>
          </div>
        )}

        <div style={{ marginTop: '24px', borderTop: '1px solid #eee', paddingTop: '16px' }}>
          <Link to="/" style={{ color: '#1B5E20', fontWeight: 600, textDecoration: 'none' }}>← Back to Home</Link>
        </div>
      </div>
    </div>
  );
};

export default PremiumSuccessPage;
